import debounce from 'lodash.debounce';
import React, {useCallback, useContext, useMemo, useRef} from 'react';

import {IconX, TextBox, TextBoxProps} from '@veneer/core';

import {GlobalTranslationsContext} from '../../../i18n';

import useStyle from './styles';

const DebouncedDateInput = ({
    id,
    value,
    onChange,
    ...properties
}: Omit<TextBoxProps, 'onChange' | 'value'> & {
    id: string;
    value?: Date;
    onChange: (value?: Date) => void;
}): JSX.Element => {
    const style = useStyle();
    const {
        veneer: {
            search: {clear},
        },
    } = useContext(GlobalTranslationsContext);

    const debouncedOnChange = useMemo(
        () =>
            debounce((date: string) => {
                onChange(date ? new Date(date) : undefined);
            }, 350),
        [onChange]
    );

    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = useCallback<Required<TextBoxProps>['onChange']>(
        date => {
            debouncedOnChange(date);
        },
        [debouncedOnChange]
    );

    const handleOnBlur = useCallback<Required<TextBoxProps>['onBlur']>(() => {
        debouncedOnChange.flush();
    }, [debouncedOnChange]);

    const handleClearMouseDown = useCallback(() => {
        // istanbul ignore else
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        debouncedOnChange('');
        debouncedOnChange.flush();
    }, [debouncedOnChange]);

    const clearButton =
        value !== undefined ? (
            <div
                aria-label={clear}
                onMouseDown={handleClearMouseDown}
                tabIndex={-1}
                title={clear}
                role="button"
            >
                <IconX />
            </div>
        ) : null;

    return (
        <TextBox
            className={style.textboxStyle}
            id={id}
            ref={inputRef}
            type="date"
            defaultValue={value?.toISOString().slice(0, 10) || ''}
            disableLabelShrinking
            onChange={handleChange}
            onBlur={handleOnBlur}
            trailingButton={clearButton}
            {...properties}
        />
    );
};

export default DebouncedDateInput;
